import React, { useCallback, useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { SectionContentDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import {
  ContentListType,
  TrackerExtraInfoProps,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';

import { isPC } from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { SectionContentItem } from 'app-components/home/sections/items/SectionContentItem';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import { getValidContentAndProfile, useRearrangedContentIds } from 'app-components/home/sections/sectionContentUtils';
import { hideScrollbar } from 'styles/common';
import { useSlide } from 'ui-hooks/useSlide';
import { useBlockProfiles } from '@/state-hooks/accounts/useBlockProfiles';

type SectionProps = {
  section: SectionContentDto;
  contents: Record<number, ContentRelayDto>;
  profileLites: Record<number, ProfileLiteRelayDto>;
  showKeyword?: boolean;
  showCategory?: boolean;
  showDescription?: boolean;
  showViewCount?: boolean;
};
const Height = 150;
const VerticalGap = 8;
const HorizontalGap = 24;

export const SectionContents = ({
  section,
  contents,
  profileLites,
  showKeyword = false,
  showCategory = false,
  showDescription = false,
  showViewCount = false,
  trackerExtraInfo,
}: SectionProps & TrackerExtraInfoProps) => {
  const { contentIds } = section;
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const { sessionSeed } = useAuthState();
  const blockedProfileIds = useBlockProfiles();
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);

  const rearrangedContentIds = useRearrangedContentIds(
    contentIds,
    section.shufflePreviewItem,
    section.previewItemCount,
    sessionSeed,
  );

  const handleOnClickTracker = useCallback(
    (contentId: number, listItemIndex: number) => {
      tracker.track(TrackingEventType.SelectContentListItem, {
        listType: ContentListType.SectionContent,
        contentId,
        listItemIndex,
        params: { sectionId: section.id, sectionDesign: section.design, sectionTitle: section.title },
        trackerExtraInfo,
      });
    },
    [section.design, section.id, section.title, tracker, trackerExtraInfo],
  );

  if (rearrangedContentIds.length === 0) {
    return null;
  }

  return (
    <div>
      <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
      <StyledWrapper>
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
        />
        <StyledScrollContainer ref={slideRef} onScroll={handleSlide} rowCount={section.rowCount}>
          {rearrangedContentIds.map((contentId, index) => {
            const validationResult = getValidContentAndProfile(contentId, contents, profileLites, blockedProfileIds);
            if (!validationResult) {
              return null;
            }

            const { content, profile } = validationResult;

            return (
              <SectionContentItem
                key={contentId}
                content={content}
                profile={profile}
                showKeyword={showKeyword}
                showCategory={showCategory}
                showDescription={showDescription}
                showViewCount={showViewCount}
                onClick={() => handleOnClickTracker(contentId, index)}
              />
            );
          })}
        </StyledScrollContainer>
      </StyledWrapper>
    </div>
  );
};

export default SectionContents;

const StyledWrapper = styled.div`
  position: relative;
`;

const StyledScrollContainer = styled.div(
  ({ rowCount = 1 }: { rowCount?: number }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-flow: column wrap;
    align-content: flex-start;
    padding: 0 16px;
    overflow-y: hidden;
    overflow-x: scroll;
    ${hideScrollbar};
    gap: ${VerticalGap}px ${HorizontalGap}px;
    height: ${Height * rowCount + VerticalGap * (rowCount - 1)}px;
    @media (${isPC}) {
      padding: 0;
      margin: 0 15px;
      overflow-x: hidden;
    }
  `,
);
